/* eslint-disable max-lines-per-function */
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';
import { ArbeitsbereichOption } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitsbereichOption';
import { Markierung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Markierung';
import { Standort as RotationsplanStandort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { LERNZIELE_MAX_LENGTH } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateVorlageStation/useForm';
import type {
  RotationsplanVorlageDetails,
  VorlageStationDetails,
} from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { useUpdateVorlageStation } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useUpdateVorlageStation/useUpdateVorlageStation';
import { useMitarbeiterAppForm } from '@/domain/mitarbeiter/shared/components/form/hooks';
import {
  Mitarbeiter_RotationsplanVorlageDauerEinheit,
  Mitarbeiter_RotationsplanVorlageErinnerungsZeit,
  Mitarbeiter_RotationsplanVorlageStationTyp,
} from '@/gql/graphql';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

export function useForm({
  vorlage,
  vorlageAbschnittId,
  station,
}: {
  vorlage: RotationsplanVorlageDetails;
  vorlageAbschnittId: string;
  station: VorlageStationDetails;
}) {
  const [updateVorlageStation] = useUpdateVorlageStation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = z
    .object({
      standort: z.instanceof(RotationsplanStandort, {
        message: t('generalValidationError.required'),
      }),
      arbeitsbereich: z.instanceof(ArbeitsbereichOption, {
        message: t('generalValidationError.required'),
      }),
      dauer: z
        .number()
        .int()
        .min(1, {
          message: t(
            'domain.mitarbeiter.rotationsplan.vorlagen.createStation.fields.dauer.min',
          ),
        }),
      dauerEinheit: z.enum(Mitarbeiter_RotationsplanVorlageDauerEinheit),
      lernziele: z.string().max(LERNZIELE_MAX_LENGTH, {
        message: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.tooLong',
        ),
      }),
      abteilungsleiter: z.instanceof(Abteilungsleiter).optional(),
      fristErinnerung: z.boolean(),
      erinnerungszeiten: z
        .enum(Mitarbeiter_RotationsplanVorlageErinnerungsZeit)
        .array(),
    })
    .superRefine((data, ctx) => {
      if (data.fristErinnerung && data.erinnerungszeiten.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.mitarbeiter.fristen.erinnerungsZeit.validationError',
          ),
          path: ['erinnerungszeiten'],
        });
      }
    });

  type FormValues = z.infer<typeof schema>;

  async function handleSubmit(value: FormValues) {
    const showError = () =>
      toast({
        variant: 'destructive',
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.updateStation.error',
        ),
      });

    try {
      const result = await updateVorlageStation({
        input: {
          vorlageStationId: station.id,
          vorlageId: vorlage.id,
          vorlageAbschnittId,
          typ: Mitarbeiter_RotationsplanVorlageStationTyp.Arbeitsbereich,
          standortId: value.standort.id,
          arbeitsbereichId: value.arbeitsbereich.id,
          abteilungsleiterId: value.abteilungsleiter?.id,
          dauer: value.dauer,
          dauerEinheit: value.dauerEinheit,
          lernziele: value.lernziele.trim(),
          erinnerungszeiten: value.fristErinnerung
            ? value.erinnerungszeiten
            : [],
        },
      });
      if (!result.data?.mitarbeiter_updateVorlageStation.success) {
        showError();
        return;
      }

      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.vorlagen.updateStation.success',
        ),
      });
      navigate(`/mitarbeiter/rotationsplaene/vorlagen/${vorlage.id}`, {
        replace: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      showError();
    }
  }

  return useMitarbeiterAppForm({
    defaultValues: {
      standort: station.standort
        ? new RotationsplanStandort({
            id: station.standort.id,
            name: station.standort.name,
            storenummer: station.standort.storenummer ?? undefined,
          })
        : undefined,
      arbeitsbereich: station.arbeitsbereich
        ? new ArbeitsbereichOption({
            id: station.arbeitsbereich.id,
            name: station.arbeitsbereich.name,
            markierung: station.arbeitsbereich
              .markierung as unknown as Markierung,
          })
        : undefined,
      dauer: station.dauer,
      dauerEinheit: station.dauerEinheit,
      lernziele: station.lernziele,
      abteilungsleiter: station.abteilungsleiter
        ? new Abteilungsleiter(station.abteilungsleiter)
        : undefined,
      fristErinnerung: station.erinnerungszeiten.length > 0,
      erinnerungszeiten: station.erinnerungszeiten,
    } as unknown as FormValues,
    validators: {
      onSubmit: schema,
    },
    onSubmit: async ({ value }) => await handleSubmit(value),
  });
}
